import React, { useEffect, useState } from "react";
import { Trophy } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import LeaderboardRow from "../components/LeaderboardRow";
import UserBetsModal from "../components/UserBetsModal";
import { Skeleton } from "../components/ui/skeleton";

export default function Leaderboard() {
  const { t } = useI18n();
  const { activeGroup } = useActiveGroup();
  const tournamentId = activeGroup?.tournament_id || null;
  const [rows, setRows] = useState([]);
  const [openUserId, setOpenUserId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRows([]);
    if (!tournamentId) return;
    (async () => {
      setLoading(true);
      const params = { tournament_id: tournamentId };
      if (activeGroup?.group_id) params.group_id = activeGroup.group_id;
      try {
        const { data } = await api.get("/leaderboard", { params });
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [tournamentId, activeGroup?.group_id]);

  return (
    <div className="px-4 pt-4" data-testid="leaderboard-page">
      <div className="mb-5">
        <div className="text-[10px] font-bold tracking-[0.05em] uppercase text-neutral-400 flex items-center gap-1">
          <Trophy size={11} weight="fill" className="text-[#41a42b]" /> {t("leaderboard_sub")}
        </div>
        <h1 className="font-display text-3xl text-[#02371b] leading-none mt-1">{t("leaderboard_title")}</h1>
      </div>

      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="space-y-0">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-3 py-3 px-3 border-b border-neutral-100 last:border-0">
                <Skeleton className="w-7 h-5 bg-neutral-200" />
                <Skeleton className="w-9 h-9 rounded-full bg-neutral-200" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="w-24 h-4 bg-neutral-200" />
                  <Skeleton className="w-12 h-3 bg-neutral-200" />
                </div>
                <Skeleton className="w-6 h-5 bg-neutral-200" />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">{t("empty_leaderboard")}</div>
        ) : (
          rows.map((r) => (
            <button
              key={r.user_id}
              type="button"
              data-testid={`leaderboard-open-${r.user_id}`}
              onClick={() => setOpenUserId(r.user_id)}
              className="w-full text-left hover:bg-neutral-50 transition-colors px-1"
            >
              <LeaderboardRow
                rank={r.rank}
                name={r.name}
                picture={r.picture}
                points={r.points}
                bets={r.bets}
                isMe={r.is_me}
              />
            </button>
          ))
        )}
      </div>

      {openUserId && tournamentId && (
        <UserBetsModal userId={openUserId} tournamentId={tournamentId} onClose={() => setOpenUserId(null)} />
      )}
    </div>
  );
}
